import React from 'react';
import { Calendar, Camera, MapPin, Tag, Info, User, Clock, FileText, Download, Share2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения основной информации из EXIF-данных
 */
const ExifOverview = ({ exifData, imageUrl }) => {
  if (!exifData) return null;

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    if (!dateString) return 'Нет данных';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Функция для скачивания изображения
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = exifData.file.name || 'image.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Изображение и общая информация */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
        {/* Превью изображения */}
        <div className="rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 h-64 flex items-center justify-center">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt="Предпросмотр изображения" 
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="text-gray-400 dark:text-gray-600">Предпросмотр недоступен</div>
          )}
        </div>

        {/* Основная информация */}
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Обзор EXIF-данных
          </h3>

          <div className="space-y-3">
            {/* Камера */}
            <div className="flex items-start">
              <Camera className="w-4 h-4 text-purple-500 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {exifData.camera.make} {exifData.camera.model}
                </p>
                {exifData.camera.lensModel && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {exifData.camera.lensModel}
                  </p>
                )}
              </div>
            </div>

            {/* Дата съемки */}
            <div className="flex items-start">
              <Calendar className="w-4 h-4 text-purple-500 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(exifData.date.original)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Дата и время съемки
                </p>
              </div>
            </div>

            {/* Настройки экспозиции */}
            <div className="flex items-start">
              <Info className="w-4 h-4 text-purple-500 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  f/{exifData.settings.aperture}, {exifData.settings.shutterSpeed}, ISO {exifData.settings.iso}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Фокусное расстояние: {exifData.settings.focalLength}мм 
                  {exifData.settings.focalLength35mm ? ` (${exifData.settings.focalLength35mm}мм в 35мм эквиваленте)` : ''}
                </p>
              </div>
            </div>

            {/* Местоположение */}
            {exifData.location.latitude && (
              <div className="flex items-start">
                <MapPin className="w-4 h-4 text-purple-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {exifData.location.city || ''} {exifData.location.country || ''}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    GPS: {exifData.location.latitude.toFixed(6)}, {exifData.location.longitude.toFixed(6)}
                  </p>
                </div>
              </div>
            )}

            {/* Автор/Копирайт */}
            {(exifData.author || exifData.copyright) && (
              <div className="flex items-start">
                <User className="w-4 h-4 text-purple-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {exifData.author || 'Автор не указан'}
                  </p>
                  {exifData.copyright && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {exifData.copyright}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Разделитель */}
      <div className="border-t border-gray-200 dark:border-gray-700"></div>

      {/* Нижняя часть с детальной информацией */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Левая колонка */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-purple-500" />
              Информация о файле
            </h4>
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Имя файла:</span>
                <span className="text-gray-900 dark:text-white font-medium">{exifData.file.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Размер файла:</span>
                <span className="text-gray-900 dark:text-white font-medium">{(exifData.file.size / (1024 * 1024)).toFixed(2)} МБ</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Тип файла:</span>
                <span className="text-gray-900 dark:text-white font-medium">{exifData.file.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Последнее изменение:</span>
                <span className="text-gray-900 dark:text-white font-medium">{formatDate(exifData.date.modified)}</span>
              </div>
            </div>
          </div>

          {/* Правая колонка */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <Clock className="w-4 h-4 mr-2 text-purple-500" />
              Информация об изображении
            </h4>
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Разрешение:</span>
                <span className="text-gray-900 dark:text-white font-medium">{exifData.image.width} × {exifData.image.height}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Ориентация:</span>
                <span className="text-gray-900 dark:text-white font-medium">{exifData.image.orientation}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Цветовое пространство:</span>
                <span className="text-gray-900 dark:text-white font-medium">{exifData.image.colorSpace}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Глубина цвета:</span>
                <span className="text-gray-900 dark:text-white font-medium">{exifData.image.bitDepth} бит</span>
              </div>
            </div>
          </div>
        </div>

        {/* Теги и ключевые слова */}
        {exifData.keywords && exifData.keywords.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <Tag className="w-4 h-4 mr-2 text-purple-500" />
              Ключевые слова
            </h4>
            <div className="flex flex-wrap gap-2">
              {exifData.keywords.map((keyword, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            onClick={handleDownload}
            variant="outline"
            icon={<Download className="w-4 h-4" />}
            size="sm"
          >
            Скачать изображение
          </Button>
          
          <Button
            onClick={() => window.print()}
            variant="outline"
            icon={<FileText className="w-4 h-4" />}
            size="sm"
          >
            Печать отчета
          </Button>
          
          <Button
            onClick={() => navigator.clipboard.writeText(JSON.stringify(exifData, null, 2))}
            variant="outline"
            icon={<Share2 className="w-4 h-4" />}
            size="sm"
          >
            Копировать данные
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExifOverview;